// src/gac/AssignLabelPanel.js
// Extracted from GlobalActionCenter.js (GAC modularization, phase 1). No behaviour change.
// This component is also the customer-portal Assign Label app (spec §3.8).
import React, { useState, useRef, useEffect } from 'react';
import axios from '../axiosInstance';
import { getSocket, initiateSocket } from '../socket';
import '../GlobalActionCenterStyles.css';

// Audio feedback for assign actions
const AudioCtx = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
const audioContext = AudioCtx ? new AudioCtx() : null;

// Unlock audio context on first user interaction (required for mobile)
const unlockAudio = () => {
    if (audioContext && audioContext.state === 'suspended') {
        audioContext.resume().then(() => {
            console.log('[Audio] Context unlocked');
        });
    }
};

const playSuccessBeep = () => {
    if (!audioContext) return;
    // Resume context if suspended (browser autoplay policy)
    if (audioContext.state === 'suspended') {
        audioContext.resume();
    }
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Pleasant ascending two-tone beep
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
    oscillator.frequency.setValueAtTime(1108, audioContext.currentTime + 0.1); // C#6

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
};

const playErrorBuzz = () => {
    if (!audioContext) return;
    if (audioContext.state === 'suspended') {
        audioContext.resume();
    }
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Low harsh buzz
    oscillator.type = 'sawtooth';
    oscillator.frequency.setValueAtTime(150, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(100, audioContext.currentTime + 0.15);

    gainNode.gain.setValueAtTime(0.25, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
};

const AssignLabelPanel = ({
    globalCompany,
    globalSelectedStore,
    executing,
    setExecuting,
    setResults,
    setErrors,
    // Which API surface to call. Internal GAC uses the default; the customer
    // portal passes 'portal/actions' so the same panel serves both surfaces.
    apiBase = 'global-action/actions',
    // The leaderboard ranks SOLUM staff against each other, which means nothing
    // to a customer looking at their own store, so the portal turns it off.
    showLeaderboard = true,
    // Copy shown while the panel has no scope to act on yet. Internal GAC makes
    // the operator choose a company AND a store; the portal derives the company
    // from the signed-in account, so naming it there would point the customer at
    // a control they do not have.
    scopePrompt = 'Select company & store first',
}) => {
    // Assign Product input refs for auto-advance
    const articleIdInputRef = useRef(null);
    const labelCodeInputRef = useRef(null);

    // Product Assignment specific state
    const [assignConfig, setAssignConfig] = useState({
        labelCode: '',
        articleId: '',
        templateName: '',
        // Padding options
        usePadding: false, // checkbox to enable padding
        padDirection: 'left', // 'left' or 'right'
        padChar: '0',
        padLength: 14, // target length
        removeChecksum: false, // remove last digit after padding
        useTemplate: false, // checkbox to enable template
        autoSubmitOnScan: true, // auto-submit when label scanned and all fields valid
        validateProduct: true, // validate product exists in SOLUM (can disable to skip)
    });

    // Real-time assignment history (via socket)
    const [assignHistory, setAssignHistory] = useState([]);
    const [historyPage, setHistoryPage] = useState(0);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyTotalPages, setHistoryTotalPages] = useState(0);
    const historyPageRef = useRef(0); // Ref for socket handler
    const HISTORY_PAGE_SIZE = 20;

    // Last assignment result for big status display
    const [lastAssignResult, setLastAssignResult] = useState(null);

    // Assignment leaderboard modal
    const [showAssignLeaderboard, setShowAssignLeaderboard] = useState(false);
    const [assignLeaderboard, setAssignLeaderboard] = useState([]);
    const [leaderboardLoading, setLeaderboardLoading] = useState(false);

    // Article validation state
    const [articleInfo, setArticleInfo] = useState(null);
    const [articleValidating, setArticleValidating] = useState(false);
    const [articleError, setArticleError] = useState(null);

    // Product ID type: 'article' (Article/UPC ID, formatted client-side) or 'ean'
    // (resolved server-side to an articleId via SaaS assign/search).
    const [idType, setIdType] = useState('article');
    const [resolvedArticleId, setResolvedArticleId] = useState(null);
    // In EAN mode an assignment needs a resolved article, not just a typed value.
    const hasProductId = idType === 'ean' ? Boolean(resolvedArticleId) : Boolean(assignConfig.articleId);

    // Label validation and template state
    const [labelInfo, setLabelInfo] = useState(null);
    const [labelValidating, setLabelValidating] = useState(false);
    const [labelError, setLabelError] = useState(null);
    const [companyConfig, setCompanyConfig] = useState(null);
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [matchingTemplates, setMatchingTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);

    // Assign Product tab state
    const [assignTab, setAssignTab] = useState('assign'); // 'assign', 'copy', or 'history'

    // Copy Label state
    const [copyNewLabelCode, setCopyNewLabelCode] = useState('');
    const [copyOldLabelCode, setCopyOldLabelCode] = useState('');
    const [copyOldLabelInfo, setCopyOldLabelInfo] = useState(null);
    const [copyOldLabelValidating, setCopyOldLabelValidating] = useState(false);
    const [copyOldLabelError, setCopyOldLabelError] = useState(null);
    const [copyExecuting, setCopyExecuting] = useState(false);
    const [lastCopyResult, setLastCopyResult] = useState(null);
    const [copyAutoSubmit, setCopyAutoSubmit] = useState(true); // auto-submit on scan
    const copyOldLabelInputRef = useRef(null);
    const copyNewLabelInputRef = useRef(null);

    // Mobile keyboard control - hidden by default for scanner use
    const [showMobileKeyboard, setShowMobileKeyboard] = useState(false);

    // Copy History state
    const [copyHistory, setCopyHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const [labelConflict, setLabelConflict] = useState(null); // 405 conflict modal

    // Fetch assignment history with pagination
    const fetchAssignHistory = async (page = 0) => {
        if (!globalCompany) return;
        try {
            const response = await axios.get(`${apiBase}/assign-history`, {
                params: {
                    companyCode: globalCompany.companyCode,
                    storeCode: globalSelectedStore?.store,
                    limit: HISTORY_PAGE_SIZE,
                    page
                }
            });
            if (response.data.success) {
                setAssignHistory(response.data.history || []);
                setHistoryTotal(response.data.total || 0);
                setHistoryTotalPages(response.data.totalPages || 0);
                setHistoryPage(page);
                historyPageRef.current = page;
            }
        } catch (error) {
            console.error('[Assign History] Failed to fetch:', error);
        }
    };

    // Refs to hold current filter values for socket handler (avoids listener recreation)
    const globalCompanyRef = useRef(globalCompany);
    const globalSelectedStoreRef = useRef(globalSelectedStore);

    useEffect(() => { globalCompanyRef.current = globalCompany; }, [globalCompany]);
    useEffect(() => { globalSelectedStoreRef.current = globalSelectedStore; }, [globalSelectedStore]);

    // Fetch initial history when action/company/store changes
    useEffect(() => {
        if (globalCompany) {
            fetchAssignHistory();
        }
    }, [globalCompany, globalSelectedStore]);

    // Listen for real-time assign history updates - single persistent listener
    useEffect(() => {
        initiateSocket(); // Ensure socket is connected
        const socket = getSocket();
        if (!socket) return;

        const handleNewAssign = (entry) => {
            console.log('[Socket] Received assignHistory:new', entry);
            // Use refs to check current filter values
            if (!globalCompanyRef.current) return;
            if (entry.companyCode !== globalCompanyRef.current.companyCode) return;
            if (globalSelectedStoreRef.current && entry.storeCode !== globalSelectedStoreRef.current.store) return;

            // Always increment total count
            setHistoryTotal(prev => prev + 1);

            // Only prepend to list if on page 0 (most recent)
            if (historyPageRef.current === 0) {
                setAssignHistory(prev => {
                    // Avoid duplicates
                    if (prev.some(h => h._id === entry._id)) return prev;
                    // Prepend and keep page size
                    return [entry, ...prev.slice(0, HISTORY_PAGE_SIZE - 1)];
                });
            }
        };

        socket.on('assignHistory:new', handleNewAssign);
        console.log('[Socket] Registered assignHistory:new listener');
        return () => {
            socket.off('assignHistory:new', handleNewAssign);
            console.log('[Socket] Unregistered assignHistory:new listener');
        };
    }, []); // Empty deps - listener is registered once

    // Fetch company config when global company changes
    useEffect(() => {
        if (globalCompany) {
            fetchCompanyConfig(globalCompany.companyCode);
        } else {
            setCompanyConfig(null);
            setAvailableTemplates([]);
            setMatchingTemplates([]);
        }
    }, [globalCompany]);

    // Format article ID with configurable padding
    const formatArticleId = (articleId, config) => {
        if (!articleId) return '';
        // Remove any non-alphanumeric characters
        let result = articleId.replace(/[^A-Za-z0-9]/g, '');

        // Remove checksum (last digit) first if enabled
        if (config.removeChecksum && result.length > 0) {
            result = result.slice(0, -1);
        }

        // If padding is disabled, return now
        if (!config.usePadding) {
            return result;
        }

        // If no padding needed (length is 0 or already at/above target)
        if (!config.padLength || config.padLength <= result.length) {
            return result;
        }

        // Pad with the specified character
        const padChar = config.padChar || '0';
        if (config.padDirection === 'right') {
            result = result.padEnd(config.padLength, padChar);
        } else {
            result = result.padStart(config.padLength, padChar);
        }

        return result;
    };

    // Preview formatted article ID
    const getFormattedArticlePreview = () => {
        if (!assignConfig.articleId) return '';
        return formatArticleId(assignConfig.articleId, assignConfig);
    };

    // Fetch company config when company is selected (for assignProduct)
    const fetchCompanyConfig = async (companyCode) => {
        try {
            const response = await axios.get(`${apiBase}/company-config`, {
                params: { companyCode }
            });
            if (response.data.success) {
                setCompanyConfig(response.data.config);
                // If templateMapping is enabled, disable template option
                if (response.data.config.templateMapping) {
                    setAssignConfig(prev => ({ ...prev, useTemplate: false }));
                }
            }
        } catch (error) {
            console.error('[Assign Product] Failed to fetch company config:', error);
        }
    };

    // Validate article ID - optionally check if product exists in SOLUM
    const validateArticleId = async (articleId) => {
        if (!articleId || articleId.trim() === '') {
            setArticleInfo(null);
            setArticleError('Article ID required');
            return false;
        }

        if (!globalCompany) {
            setArticleError('Select a company first');
            return false;
        }

        if (!globalSelectedStore) {
            setArticleError('Select a store first');
            return false;
        }

        // EAN mode: resolve to an article via SaaS; padding/checksum/validate options don't apply.
        if (idType === 'ean') {
            const ean = articleId.trim();
            setArticleValidating(true);
            setArticleError(null);
            setArticleInfo(null);
            setResolvedArticleId(null);

            try {
                const response = await axios.get(`${apiBase}/article-info-by-ean`, {
                    params: {
                        ean,
                        companyCode: globalCompany.companyCode,
                        storeCode: globalSelectedStore.store
                    }
                });

                const info = response.data.articleInfo;
                if (response.data.success && info?.articleId) {
                    setResolvedArticleId(info.articleId);
                    setArticleInfo({ ...info, name: `${info.articleName || info.articleId} (article ${info.articleId})` });
                    setArticleError(null);
                    setArticleValidating(false);
                    setTimeout(() => labelCodeInputRef.current?.focus(), 50);
                    return true;
                }

                setArticleError(response.data.message || `No article mapped to EAN ${ean} in this store`);
                setArticleValidating(false);
                setTimeout(() => articleIdInputRef.current?.select(), 50);
                return false;
            } catch (error) {
                console.error('[EAN Validation] Error:', error);
                setArticleError(error.response?.data?.message || 'EAN lookup failed');
                setArticleValidating(false);
                setTimeout(() => articleIdInputRef.current?.select(), 50);
                return false;
            }
        }

        const formattedId = formatArticleId(articleId, assignConfig);

        // If validation disabled, just show formatted preview and advance
        if (!assignConfig.validateProduct) {
            setArticleInfo({ articleId: formattedId, name: `${formattedId}` });
            setArticleError(null);
            setTimeout(() => labelCodeInputRef.current?.focus(), 50);
            return true;
        }

        // Validate against SOLUM API
        setArticleValidating(true);
        setArticleError(null);
        setArticleInfo(null);

        try {
            const response = await axios.get(`${apiBase}/article-info`, {
                params: {
                    articleId: formattedId,
                    companyCode: globalCompany.companyCode,
                    storeCode: globalSelectedStore.store
                }
            });

            if (response.data.success) {
                setArticleInfo(response.data.articleInfo);
                setArticleError(null);
                setArticleValidating(false);
                // Advance to label field on success
                setTimeout(() => labelCodeInputRef.current?.focus(), 50);
                return true;
            } else {
                setArticleError('Product not found');
                setArticleInfo(null);
                setArticleValidating(false);
                // Stay on article field, select text for easy re-scan
                setTimeout(() => articleIdInputRef.current?.select(), 50);
                return false;
            }
        } catch (error) {
            console.error('[Article Validation] Error:', error);
            setArticleError(error.response?.data?.message || 'Validation failed');
            setArticleInfo(null);
            setArticleValidating(false);
            // Stay on article field on error
            setTimeout(() => articleIdInputRef.current?.select(), 50);
            return false;
        }
    };

    // Validate label code on blur
    const validateLabelCode = async (labelCode) => {
        if (!labelCode || labelCode.length !== 12) {
            setLabelInfo(null);
            setLabelError(null);
            return;
        }

        if (!globalCompany) {
            setLabelError('Select a company first');
            return;
        }

        if (!globalSelectedStore) {
            setLabelError('Select a store first');
            return;
        }

        setLabelValidating(true);
        setLabelError(null);
        setLabelInfo(null);

        try {
            const params = {
                labelCode,
                companyCode: globalCompany.companyCode
            };

            // Use label-store-info to get articleId as well
            const response = await axios.get(`${apiBase}/label-store-info`, { params });

            if (response.data.success) {
                setLabelInfo(response.data.labelInfo);
                setLabelError(null);
                // If template is enabled, filter templates based on label type
                if (assignConfig.useTemplate && availableTemplates.length > 0) {
                    filterTemplatesForLabel(response.data.labelInfo);
                }

                // Auto-submit if enabled and all fields are valid
                if (assignConfig.autoSubmitOnScan && hasProductId && globalCompany && globalSelectedStore) {
                    setLabelValidating(false);
                    // Small delay to let state update
                    setTimeout(() => {
                        executeAssignProductDirect(labelCode);
                    }, 100);
                    return;
                }
            } else {
                setLabelError(response.data.message || 'Label not found');
                setLabelInfo(null);
            }
        } catch (error) {
            console.error('[Assign Product] Label validation error:', error);
            setLabelError(error.response?.data?.message || 'Failed to validate label');
            setLabelInfo(null);
        } finally {
            setLabelValidating(false);
        }
    };

    // Fetch templates for company
    const fetchTemplates = async (companyCode) => {
        setTemplatesLoading(true);
        try {
            const response = await axios.get(`${apiBase}/templates`, {
                params: { companyCode }
            });
            if (response.data.success) {
                setAvailableTemplates(response.data.templates);
                // If we have label info, filter immediately
                if (labelInfo) {
                    filterTemplatesForLabel(labelInfo, response.data.templates);
                } else {
                    setMatchingTemplates(response.data.templates);
                }
            }
        } catch (error) {
            console.error('[Assign Product] Failed to fetch templates:', error);
        } finally {
            setTemplatesLoading(false);
        }
    };

    // Filter templates based on label type (e.g., "2.9BWYR")
    const filterTemplatesForLabel = (label, templates = availableTemplates) => {
        if (!label || !templates.length) {
            setMatchingTemplates([]);
            return;
        }

        // Parse label type like "2.9BWYR" into size and color
        const labelType = label.type || '';
        // Extract size (numbers at start) and color (letters at end)
        const sizeMatch = labelType.match(/^(\d+\.?\d*)/);
        const colorMatch = labelType.match(/([A-Z]+)$/);
        const labelSize = sizeMatch ? sizeMatch[1] : '';
        const labelColor = colorMatch ? colorMatch[1] : '';

        console.log(`[Template Filter] Label type: ${labelType}, size: ${labelSize}, color: ${labelColor}`);

        const matching = templates.filter(t => {
            // templateSize is like "2.9" or "4.2 / 4.2_4C"
            const templateSize = t.templateSize || '';
            const templateColor = t.color || '';

            // Check if template size contains the label size
            const sizeMatches = templateSize.includes(labelSize);
            // Check if template color matches (or is compatible)
            const colorMatches = !labelColor || !templateColor ||
                templateColor.toUpperCase().split('').every(c => labelColor.includes(c));

            return sizeMatches && colorMatches;
        });

        console.log(`[Template Filter] Found ${matching.length} matching templates`);
        setMatchingTemplates(matching);
    };

    // Handle template checkbox change
    const handleTemplateCheckboxChange = async (checked) => {
        setAssignConfig(prev => ({ ...prev, useTemplate: checked }));
        if (checked && globalCompany) {
            await fetchTemplates(globalCompany.companyCode);
        }
    };

    // Fetch copy history from MongoDB
    const fetchCopyHistory = async () => {
        if (!globalCompany) return;

        setHistoryLoading(true);
        try {
            const response = await axios.get(`${apiBase}/copy-history`, {
                params: {
                    companyCode: globalCompany.companyCode,
                    storeCode: globalSelectedStore?.store
                }
            });
            if (response.data.success) {
                setCopyHistory(response.data.history || []);
            }
        } catch (error) {
            console.error('[Copy History] Failed to fetch:', error);
        } finally {
            setHistoryLoading(false);
        }
    };

    // Fetch assignment leaderboard
    const fetchAssignLeaderboard = async () => {
        if (!globalCompany) return;
        setLeaderboardLoading(true);
        try {
            const response = await axios.get(`${apiBase}/assign-leaderboard`, {
                params: { companyCode: globalCompany.companyCode }
            });
            if (response.data.success) {
                setAssignLeaderboard(response.data.leaderboard || []);
            }
        } catch (error) {
            console.error('[Assign Leaderboard] Failed to fetch:', error);
        } finally {
            setLeaderboardLoading(false);
        }
    };

    // Validate old label for copy (fetch its article info)
    const validateOldLabelForCopy = async (labelCode) => {
        if (!labelCode || labelCode.length !== 12) {
            setCopyOldLabelInfo(null);
            setCopyOldLabelError(null);
            return;
        }

        if (!globalCompany) {
            setCopyOldLabelError(scopePrompt);
            return;
        }

        setCopyOldLabelValidating(true);
        setCopyOldLabelError(null);
        setCopyOldLabelInfo(null);
        setResults(null); // Clear previous results so source info shows

        try {
            // Use /labels/store endpoint - validates label exists AND returns articleId
            const response = await axios.get(`${apiBase}/label-store-info`, {
                params: {
                    labelCode,
                    companyCode: globalCompany.companyCode
                }
            });

            if (response.data.success && response.data.labelInfo) {
                const labelData = response.data.labelInfo;

                // Check if label has an article assigned
                if (!labelData.articleId || labelData.articleId.length === 0) {
                    setCopyOldLabelError('Source label has no article assigned');
                    setTimeout(() => copyOldLabelInputRef.current?.select(), 50);
                    return;
                }

                setCopyOldLabelInfo(labelData);
                // Auto-focus the new label input for scanner workflow
                setTimeout(() => {
                    copyNewLabelInputRef.current?.focus();
                }, 100);
            } else {
                setCopyOldLabelError(response.data.message || 'Label not found');
                setTimeout(() => copyOldLabelInputRef.current?.select(), 50);
            }
        } catch (error) {
            console.error('[Copy Label] Validation error:', error);
            setCopyOldLabelError(error.response?.data?.message || 'Failed to validate label');
            setTimeout(() => copyOldLabelInputRef.current?.select(), 50);
        } finally {
            setCopyOldLabelValidating(false);
        }
    };

    // Execute Copy Label
    const executeCopyLabel = async () => {
        if (!copyOldLabelInfo || !copyNewLabelCode) return;

        const articleId = copyOldLabelInfo.articleId?.[0] || copyOldLabelInfo.articleId;
        if (!articleId) {
            alert('Source label has no product assigned');
            return;
        }

        setCopyExecuting(true);

        try {
            const payload = {
                companyCode: globalCompany.companyCode,
                storeCode: globalSelectedStore.store,
                labelCode: copyNewLabelCode,
                articleId: articleId,
                assignType: 'copy'
            };

            const response = await axios.post(`${apiBase}/assign-product`, payload);

            setResults({
                assignResult: {
                    success: response.data.success,
                    message: response.data.success
                        ? `Copied article ${articleId} from ${copyOldLabelCode} to ${copyNewLabelCode}`
                        : response.data.message
                },
                details: {
                    company: globalCompany.companyCode,
                    store: globalSelectedStore.store,
                    sourceLabel: copyOldLabelCode,
                    targetLabel: copyNewLabelCode,
                    articleId: articleId
                }
            });

            // Set result for display
            setLastCopyResult({
                success: response.data.success,
                sourceLabel: copyOldLabelCode,
                targetLabel: copyNewLabelCode,
                articleId: articleId,
                message: response.data.message
            });

            if (response.data.success) {
                // Save to history
                try {
                    await axios.post(`${apiBase}/copy-history`, {
                        companyCode: globalCompany.companyCode,
                        storeCode: globalSelectedStore.store,
                        sourceLabel: copyOldLabelCode,
                        targetLabel: copyNewLabelCode,
                        articleId: articleId,
                        labelType: copyOldLabelInfo.type || copyOldLabelInfo.labelModel
                    });
                } catch (histErr) {
                    console.error('[Copy History] Failed to save:', histErr);
                }

                // Clear inputs on success and reset for next copy cycle
                setCopyNewLabelCode('');
                setCopyOldLabelCode('');
                setCopyOldLabelInfo(null);
                // Reset focus to source label for next copy
                setTimeout(() => copyOldLabelInputRef.current?.focus(), 100);
            } else {
                setErrors([response.data.message || 'Copy failed']);
            }
        } catch (error) {
            console.error('[Copy Label] Error:', error);
            setLastCopyResult({
                success: false,
                sourceLabel: copyOldLabelCode,
                targetLabel: copyNewLabelCode,
                articleId: articleId,
                message: error.response?.data?.message || 'Failed to copy label'
            });
            setErrors([error.response?.data?.message || 'Failed to copy label']);
        } finally {
            setCopyExecuting(false);
        }
    };

    // Execute Product Assignment (direct version for auto-submit, takes labelCode as param)
    const executeAssignProductDirect = async (labelCodeOverride) => {
        const labelCode = labelCodeOverride || assignConfig.labelCode;

        if (!globalCompany || !globalSelectedStore || !labelCode || labelCode.length !== 12 || !hasProductId) {
            return; // Silently fail for auto-submit if conditions not met
        }

        setExecuting(true);
        setErrors([]);

        // EAN mode assigns the article SaaS resolved, never the scanned EAN itself.
        const formattedArticleId = idType === 'ean'
            ? resolvedArticleId
            : formatArticleId(assignConfig.articleId, assignConfig);

        try {
            const payload = {
                companyCode: globalCompany.companyCode,
                storeCode: globalSelectedStore.store,
                labelCode: labelCode,
                articleId: formattedArticleId,
                templateName: assignConfig.useTemplate && assignConfig.templateName ? assignConfig.templateName : undefined
            };

            const response = await axios.post(`${apiBase}/assign-product`, payload);

            // Set last result for big status display
            setLastAssignResult({
                success: response.data.success,
                labelCode: labelCode,
                articleId: formattedArticleId,
                message: response.data.success ? 'SUCCESS' : (response.data.message || 'Assignment failed'),
                timestamp: new Date()
            });

            // Add to local history immediately for responsiveness
            const historyEntry = {
                _id: Date.now().toString(),
                labelCode: labelCode,
                articleId: formattedArticleId,
                storeCode: globalSelectedStore.store,
                success: response.data.success,
                userName: 'You',
                userEmail: 'You',
                createdAt: new Date().toISOString(),
                error: response.data.success ? null : (response.data.message || 'Assignment failed')
            };
            setAssignHistory(prev => [historyEntry, ...prev.slice(0, 19)]);

            if (response.data.success) {
                playSuccessBeep();
                // Clear both fields and reset for next assignment
                setAssignConfig(prev => ({ ...prev, labelCode: '', articleId: '' }));
                setLabelInfo(null);
                setLabelError(null);
                setArticleInfo(null);
                setArticleError(null);
                setResolvedArticleId(null);
                // Focus back to article ID input for next scan
                setTimeout(() => articleIdInputRef.current?.focus(), 100);
            } else {
                playErrorBuzz();
                setErrors([response.data.message || 'Assignment failed']);
            }
        } catch (error) {
            playErrorBuzz();
            const errorMsg = error.response?.data?.message || error.message;
            const errorData = error.response?.data;

            // Check for 405 label conflict - show alert modal
            if (error.response?.status === 405 || errorData?.errorType === 'LABEL_CONFLICT') {
                setLabelConflict({
                    labelCode: labelCode,
                    message: errorMsg,
                    conflictStore: errorData?.details?.conflictStore || null,
                    suggestion: errorData?.suggestion || 'Unassign the label from its current store first.'
                });
            }

            // Show the error in the errors list
            setErrors([`Assignment failed: ${errorMsg}`]);

            // Clear the label field so they can scan another
            setAssignConfig(prev => ({ ...prev, labelCode: '' }));
            setLabelInfo(null);
            setLabelError(null);

            // Set last result for big status display
            setLastAssignResult({
                success: false,
                labelCode: labelCode,
                articleId: formattedArticleId,
                message: errorMsg,
                timestamp: new Date()
            });

            // Add failed entry to history
            const historyEntry = {
                _id: Date.now().toString(),
                labelCode: labelCode,
                articleId: formattedArticleId,
                storeCode: globalSelectedStore.store,
                success: false,
                error: errorMsg,
                userName: 'You',
                userEmail: 'You',
                createdAt: new Date().toISOString()
            };
            setAssignHistory(prev => [historyEntry, ...prev.slice(0, 19)]);
        } finally {
            setExecuting(false);
        }
    };

    // Execute Product Assignment (button click version)
    const executeAssignProduct = async () => {
        if (!globalCompany) {
            alert('Please select a company first');
            return;
        }
        if (!globalSelectedStore) {
            alert('Please select a store first');
            return;
        }
        if (!assignConfig.labelCode || assignConfig.labelCode.length !== 12) {
            alert('Label code must be exactly 12 characters');
            return;
        }
        if (!hasProductId) {
            alert(idType === 'ean' ? 'Scan an EAN and wait for it to resolve to an article' : 'Article/UPC ID is required');
            return;
        }
        await executeAssignProductDirect(assignConfig.labelCode);
    };

    return (
        <>
                <div className="blink-led-container assign-product-container">
                    {/* Left Panel - Configuration & Input */}
                    <div className="action-config-section blink-led-section">
                        {/* Tabs */}
                        <div className="assign-tabs">
                            <button
                                className={`assign-tab ${assignTab === 'assign' ? 'active' : ''}`}
                                onClick={() => setAssignTab('assign')}
                            >
                                Assign Product
                            </button>
                            <button
                                className={`assign-tab ${assignTab === 'copy' ? 'active' : ''}`}
                                onClick={() => setAssignTab('copy')}
                            >
                                Copy Label
                            </button>
                            {showLeaderboard && (
                                <button
                                    className="leaderboard-btn tab-btn"
                                    onClick={() => {
                                        fetchAssignLeaderboard();
                                        setShowAssignLeaderboard(true);
                                    }}
                                >
                                    🏆 Leaderboard
                                </button>
                            )}
                            {/* Mobile Input Mode Toggle - Scanner (default) or Keyboard */}
                            <button
                                className={`input-mode-toggle ${showMobileKeyboard ? 'keyboard-mode' : 'scanner-mode'}`}
                                onClick={() => {
                                    unlockAudio();
                                    setShowMobileKeyboard(prev => !prev);
                                }}
                            >
                                {showMobileKeyboard ? '⌨️ Keyboard' : '📱 Scanner'}
                            </button>
                        </div>

                        {/* Assign Tab Content */}
                        {assignTab === 'assign' && (
                        <div className="config-panel">
                            {/* Article ID Input - FIRST (scan product barcode first) */}
                            <div className="config-row">
                                <label className="id-type-label">
                                    <select
                                        className="id-type-select"
                                        aria-label="ID type"
                                        value={idType}
                                        onChange={(e) => {
                                            setIdType(e.target.value);
                                            // Switching modes invalidates whatever was typed/resolved
                                            setAssignConfig(prev => ({ ...prev, articleId: '' }));
                                            setArticleInfo(null);
                                            setArticleError(null);
                                            setResolvedArticleId(null);
                                            setTimeout(() => articleIdInputRef.current?.focus(), 50);
                                        }}
                                    >
                                        <option value="article">Article/UPC ID</option>
                                        <option value="ean">EAN</option>
                                    </select>
                                    :
                                </label>
                                <div className="article-id-wrapper inline-validation">
                                    <input
                                        ref={articleIdInputRef}
                                        type="text"
                                        inputMode={showMobileKeyboard ? "text" : "none"}
                                        className={`assign-input ${(!globalCompany || !globalSelectedStore) ? 'disabled' : ''} ${articleError ? 'invalid' : ''} ${articleInfo ? 'valid' : ''}`}
                                        placeholder={(!globalCompany || !globalSelectedStore) ? scopePrompt : (idType === 'ean' ? "Scan or type EAN" : "Scan or type barcode first")}
                                        value={assignConfig.articleId}
                                        onFocus={unlockAudio}
                                        onChange={(e) => {
                                            setAssignConfig(prev => ({ ...prev, articleId: e.target.value }));
                                            // Clear validation state when typing
                                            setArticleInfo(null);
                                            setArticleError(null);
                                            setResolvedArticleId(null);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && e.target.value) {
                                                e.preventDefault();
                                                validateArticleId(e.target.value);
                                            }
                                        }}
                                        onBlur={(e) => {
                                            if (e.target.value && !articleInfo) {
                                                validateArticleId(e.target.value);
                                            }
                                        }}
                                        disabled={!globalCompany || !globalSelectedStore}
                                    />
                                    {articleValidating && <span className="validating-indicator">Validating...</span>}
                                    {articleError && <span className="article-validation-error inline">{articleError}</span>}
                                    {articleInfo && <span className="article-validation-success inline">✓ {articleInfo.name || articleInfo.articleName || 'Product found'}</span>}
                                </div>
                            </div>

                            {/* Label Code Input - SECOND (scan label to validate & assign) */}
                            <div className="config-row">
                                <label>Label Code:</label>
                                <div className="label-code-wrapper">
                                    <div className="input-with-counter">
                                        <input
                                            ref={labelCodeInputRef}
                                            type="text"
                                            inputMode={showMobileKeyboard ? "text" : "none"}
                                            className={`assign-input ${(!globalCompany || !globalSelectedStore) ? 'disabled' : ''} ${assignConfig.labelCode && assignConfig.labelCode.length !== 12 ? 'invalid' : ''} ${labelError ? 'invalid' : ''} ${labelInfo ? 'valid' : ''}`}
                                            placeholder={(!globalCompany || !globalSelectedStore) ? scopePrompt : "Scan or type ESL label"}
                                            value={assignConfig.labelCode}
                                            onChange={(e) => {
                                                const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                                setAssignConfig(prev => ({ ...prev, labelCode: val }));
                                                setLabelInfo(null);
                                                setLabelError(null);
                                            }}
                                            onKeyDown={(e) => {
                                                // Submit on Enter (scanner sends Enter after scan)
                                                if (e.key === 'Enter' && assignConfig.labelCode.length === 12) {
                                                    e.preventDefault();
                                                    if (assignConfig.autoSubmitOnScan && hasProductId) {
                                                        executeAssignProductDirect(assignConfig.labelCode);
                                                    } else {
                                                        validateLabelCode(assignConfig.labelCode);
                                                    }
                                                }
                                            }}
                                            maxLength={12}
                                            disabled={!globalCompany || !globalSelectedStore}
                                        />
                                        <span className={`char-counter ${assignConfig.labelCode.length === 12 ? 'valid' : ''}`}>
                                            {assignConfig.labelCode.length}/12
                                        </span>
                                        {labelValidating && <span className="validating-indicator">Validating...</span>}
                                        {executing && <span className="validating-indicator">Assigning...</span>}
                                    </div>
                                    {labelError && (
                                        <div className="label-validation-error">{labelError}</div>
                                    )}
                                    {labelInfo && !assignConfig.autoSubmitOnScan && (
                                        <div className="label-validation-success">
                                            <span>{labelInfo.type || labelInfo.labelModel}</span>
                                            {labelInfo.articleId && labelInfo.articleId.length > 0 && (
                                                <span className="current-product">
                                                    📦 {Array.isArray(labelInfo.articleId) ? labelInfo.articleId.join(', ') : labelInfo.articleId}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Padding Options Row */}
                            <div className="config-row padding-options-row">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={assignConfig.usePadding}
                                        onChange={(e) => setAssignConfig(prev => ({ ...prev, usePadding: e.target.checked }))}
                                        style={{ marginRight: '6px' }}
                                        disabled={idType === 'ean'}
                                    />
                                    Pad:
                                </label>
                                <div className={`padding-options-inline ${(!assignConfig.usePadding || idType === 'ean') ? 'disabled' : ''}`}>
                                    <select
                                        className="pad-direction-select"
                                        value={assignConfig.padDirection}
                                        onChange={(e) => setAssignConfig(prev => ({ ...prev, padDirection: e.target.value }))}
                                        disabled={!assignConfig.usePadding || idType === 'ean'}
                                    >
                                        <option value="left">Left</option>
                                        <option value="right">Right</option>
                                    </select>
                                    <span className="padding-label">with</span>
                                    <input
                                        type="text"
                                        className="pad-char-input"
                                        value={assignConfig.padChar}
                                        onChange={(e) => setAssignConfig(prev => ({ ...prev, padChar: e.target.value.slice(0, 1) }))}
                                        maxLength={1}
                                        placeholder="0"
                                        disabled={!assignConfig.usePadding || idType === 'ean'}
                                    />
                                    <span className="padding-label">to</span>
                                    <input
                                        type="number"
                                        className="pad-length-input"
                                        value={assignConfig.padLength}
                                        onChange={(e) => setAssignConfig(prev => ({ ...prev, padLength: parseInt(e.target.value) || 0 }))}
                                        min={0}
                                        max={50}
                                        placeholder="14"
                                        disabled={!assignConfig.usePadding || idType === 'ean'}
                                    />
                                    <span className="padding-label">digits</span>
                                </div>
                            </div>

                            {/* Remove Checksum Option */}
                            <div className="config-row">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={assignConfig.removeChecksum}
                                        onChange={(e) => setAssignConfig(prev => ({ ...prev, removeChecksum: e.target.checked }))}
                                        style={{ marginRight: '6px' }}
                                        disabled={idType === 'ean'}
                                    />
                                    Remove checksum (last digit)
                                </label>
                            </div>

                            {/* Validate Product Option */}
                            <div className="config-row">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={assignConfig.validateProduct}
                                        onChange={(e) => setAssignConfig(prev => ({ ...prev, validateProduct: e.target.checked }))}
                                        style={{ marginRight: '6px' }}
                                        disabled={idType === 'ean'}
                                    />
                                    Validate product ID
                                    {/* Not optional in EAN mode: SOLUM links by articleId, so an
                                        EAN has to be resolved to one before anything can be
                                        assigned — that lookup IS the validation. An Article/UPC
                                        value is already the articleId, so the check can be skipped. */}
                                    {idType === 'ean' && (
                                        <em style={{ marginLeft: 6, fontSize: '0.85em', opacity: 0.75 }}>
                                            (always on for EAN — the lookup is what finds the product)
                                        </em>
                                    )}
                                </label>
                            </div>

                            {/* Auto Submit on Scan */}
                            <div className="config-row">
                                <label className={`auto-submit-label ${assignConfig.autoSubmitOnScan ? 'active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={assignConfig.autoSubmitOnScan}
                                        onChange={(e) => setAssignConfig(prev => ({ ...prev, autoSubmitOnScan: e.target.checked }))}
                                        style={{ marginRight: '6px' }}
                                    />
                                    Auto-submit on scan
                                    {assignConfig.autoSubmitOnScan && <span className="auto-submit-hint"> (scan label to assign instantly)</span>}
                                </label>
                            </div>

                            {/* Formatted Preview */}
                            {assignConfig.articleId && idType !== 'ean' && (
                                <div className="config-row">
                                    <label>Formatted ID:</label>
                                    <div className="formatted-preview">
                                        <code>{getFormattedArticlePreview()}</code>
                                        <span className="preview-length">({getFormattedArticlePreview().length} chars)</span>
                                    </div>
                                </div>
                            )}

                            {/* Template Name Input (Optional) - Hidden if templateMapping is enabled */}
                            {(!companyConfig || !companyConfig.templateMapping) && (
                                <div className="config-row template-row">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={assignConfig.useTemplate}
                                            onChange={(e) => handleTemplateCheckboxChange(e.target.checked)}
                                            style={{ marginRight: '6px' }}
                                            disabled={!labelInfo}
                                        />
                                        Template:
                                        {!labelInfo && <span className="hint-text"> (validate label first)</span>}
                                    </label>
                                    {assignConfig.useTemplate && (
                                        <div className="template-select-wrapper">
                                            {templatesLoading ? (
                                                <span className="loading-text">Loading templates...</span>
                                            ) : matchingTemplates.length > 0 ? (
                                                <select
                                                    className="assign-input template-select"
                                                    value={assignConfig.templateName}
                                                    onChange={(e) => setAssignConfig(prev => ({ ...prev, templateName: e.target.value }))}
                                                >
                                                    <option value="">-- Select Template --</option>
                                                    {matchingTemplates.map(t => (
                                                        <option key={t.typeId} value={t.typeName}>
                                                            {t.typeName} ({t.templateSize || `${t.width}x${t.height}`})
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className="no-templates-text">No matching templates found</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            {companyConfig?.templateMapping && (
                                <div className="config-row">
                                    <span className="template-mapping-notice">Template mapping is enabled for this company - templates are auto-assigned</span>
                                </div>
                            )}

                            {/* Assign Button */}
                            <div className="config-row">
                                <button
                                    className="execute-button blink-led-button"
                                    onClick={executeAssignProduct}
                                    disabled={
                                        executing ||
                                        !globalCompany ||
                                        !globalSelectedStore ||
                                        !assignConfig.labelCode ||
                                        assignConfig.labelCode.length !== 12 ||
                                        !hasProductId
                                    }
                                >
                                    {executing ? 'Assigning...' : 'Assign Product to Label'}
                                </button>
                            </div>
                        </div>
                        )}

                        {/* Copy Tab Content */}
                        {assignTab === 'copy' && (
                        <div className="config-panel">
                            {/* Source Label Code (source) - FIRST */}
                            <div className="config-row">
                                <label>Source Label:</label>
                                <div className="label-code-wrapper">
                                    <div className="input-with-counter">
                                        <input
                                            ref={copyOldLabelInputRef}
                                            type="text"
                                            inputMode={showMobileKeyboard ? "text" : "none"}
                                            className={`assign-input ${copyOldLabelCode && copyOldLabelCode.length !== 12 ? 'invalid' : ''} ${copyOldLabelError ? 'invalid' : ''} ${copyOldLabelInfo ? 'valid' : ''}`}
                                            placeholder="Scan or type source label"
                                            value={copyOldLabelCode}
                                            onChange={(e) => {
                                                const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                                setCopyOldLabelCode(val);
                                                setCopyOldLabelInfo(null);
                                                setCopyOldLabelError(null);
                                                // Auto-validate when 12 chars reached
                                                if (val.length === 12) {
                                                    setTimeout(() => validateOldLabelForCopy(val), 100);
                                                }
                                            }}
                                            maxLength={12}
                                        />
                                        <span className={`char-counter ${copyOldLabelCode.length === 12 ? 'valid' : ''}`}>
                                            {copyOldLabelCode.length}/12
                                        </span>
                                        {copyOldLabelValidating && <span className="validating-indicator">Validating...</span>}
                                    </div>
                                    {copyOldLabelError && (
                                        <div className="label-validation-error">{copyOldLabelError}</div>
                                    )}
                                    {copyOldLabelInfo && (
                                        <div className="label-validation-success">
                                            <span>✓ {copyOldLabelInfo.type || copyOldLabelInfo.labelModel}</span>
                                            {copyOldLabelInfo.articleId && copyOldLabelInfo.articleId.length > 0 && (
                                                <span className="current-product">
                                                    📦 {Array.isArray(copyOldLabelInfo.articleId) ? copyOldLabelInfo.articleId.join(', ') : copyOldLabelInfo.articleId}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* New Label Code (target) - SECOND, disabled until source is validated */}
                            <div className="config-row">
                                <label>New Label Code:</label>
                                <div className="input-with-counter">
                                    <input
                                        ref={copyNewLabelInputRef}
                                        type="text"
                                        inputMode={showMobileKeyboard ? "text" : "none"}
                                        className={`assign-input ${copyNewLabelCode && copyNewLabelCode.length !== 12 ? 'invalid' : ''} ${!copyOldLabelInfo ? 'disabled' : ''}`}
                                        placeholder={copyOldLabelInfo ? "Scan or type new label" : "Validate source first"}
                                        value={copyNewLabelCode}
                                        onChange={(e) => {
                                            const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                            setCopyNewLabelCode(val);
                                        }}
                                        onKeyDown={(e) => {
                                            // Auto-execute copy on Enter (scanner sends Enter after scan)
                                            if (e.key === 'Enter' && copyAutoSubmit && copyNewLabelCode.length === 12 && copyOldLabelInfo && !copyExecuting) {
                                                e.preventDefault();
                                                executeCopyLabel();
                                            }
                                        }}
                                        maxLength={12}
                                        disabled={!copyOldLabelInfo}
                                    />
                                    <span className={`char-counter ${copyNewLabelCode.length === 12 ? 'valid' : ''}`}>
                                        {copyNewLabelCode.length}/12
                                    </span>
                                </div>
                            </div>

                            {/* Copy Button */}
                            {copyOldLabelInfo && copyNewLabelCode.length === 12 && (
                                <div className="config-row">
                                    <button
                                        className="execute-button blink-led-button"
                                        onClick={executeCopyLabel}
                                        disabled={copyExecuting}
                                    >
                                        {copyExecuting ? 'Copying...' : 'Copy Label'}
                                    </button>
                                </div>
                            )}

                            {/* Auto-submit option */}
                            <div className="config-row">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={copyAutoSubmit}
                                        onChange={(e) => setCopyAutoSubmit(e.target.checked)}
                                    />
                                    Auto-submit on scan
                                </label>
                            </div>

                            <div className="config-row copy-instructions">
                                <p>1. Scan the SOURCE label (to copy from)</p>
                                <p>2. Scan the NEW label (destination)</p>
                            </div>
                        </div>
                        )}

                    </div>

                    {/* Right Panel - Results/History */}
                    <div className="action-config-section blink-led-results-section">
                        <div className="assign-results-header">
                            <h3>Live History</h3>
                        </div>
                        <div className="config-panel">
                            {assignTab === 'assign' ? (
                                /* Real-time assignment history for assign tab */
                                <div className="assign-history-panel">
                                    {/* Big Status Display */}
                                    {lastAssignResult && (
                                        <div className={`assign-result-banner ${lastAssignResult.success ? 'success' : 'error'}`}>
                                            <div className="result-status">
                                                <span className="status-icon">{lastAssignResult.success ? '✓' : '✗'}</span>
                                                <span className="status-text">{lastAssignResult.success ? 'SUCCESS' : 'FAILED'}</span>
                                            </div>
                                            <div className="result-details">
                                                <div><strong>Label:</strong> {lastAssignResult.labelCode}</div>
                                                <div><strong>Article:</strong> {lastAssignResult.articleId}</div>
                                                {!lastAssignResult.success && lastAssignResult.message && (
                                                    <div className="result-error">{lastAssignResult.message}</div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* History Table */}
                                    <div className="assign-history-table-wrapper">
                                        <table className="assign-history-table">
                                            <thead>
                                                <tr>
                                                    <th></th>
                                                    <th>Type</th>
                                                    <th>Assigned By</th>
                                                    <th>Article ID</th>
                                                    <th>Label Code</th>
                                                    <th>Date/Time</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {assignHistory.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="6" className="history-empty-cell">Assignments will appear here in real-time</td>
                                                    </tr>
                                                ) : (
                                                    assignHistory.map((entry, idx) => (
                                                        <tr key={entry._id || idx} className={entry.success ? 'success-row' : 'error-row'}>
                                                            <td className="status-cell">
                                                                <span className={`status-badge ${entry.success ? 'success' : 'error'}`}>
                                                                    {entry.success ? '✓' : '✗'}
                                                                </span>
                                                            </td>
                                                            <td className={`type-cell ${entry.assignType === 'copy' ? 'copy-type' : 'new-type'}`}>
                                                                {entry.assignType === 'copy' ? 'Copy' : 'New'}
                                                            </td>
                                                            <td>{entry.userName || entry.userEmail?.split('@')[0] || 'Unknown'}</td>
                                                            <td className="monospace">{entry.articleId}</td>
                                                            <td className="monospace">{entry.labelCode}</td>
                                                            <td>{new Date(entry.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {new Date(entry.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination Controls */}
                                    {historyTotal > HISTORY_PAGE_SIZE && (
                                        <div className="history-pagination">
                                            <button
                                                className="pagination-btn"
                                                onClick={() => fetchAssignHistory(historyPage - 1)}
                                                disabled={historyPage === 0}
                                            >
                                                ← Newer
                                            </button>
                                            <span className="pagination-info">
                                                Page {historyPage + 1} of {historyTotalPages} ({historyTotal.toLocaleString()} total)
                                            </span>
                                            <button
                                                className="pagination-btn"
                                                onClick={() => fetchAssignHistory(historyPage + 1)}
                                                disabled={historyPage >= historyTotalPages - 1}
                                            >
                                                Older →
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : assignTab === 'copy' ? (
                                /* Copy tab results */
                                <div className="copy-results-panel">
                                    {/* Big Status Display for Copy */}
                                    {lastCopyResult && (
                                        <div className={`assign-result-banner ${lastCopyResult.success ? 'success' : 'error'}`}>
                                            <div className="result-status">
                                                <span className="status-icon">{lastCopyResult.success ? '✓' : '✗'}</span>
                                                <span className="status-text">{lastCopyResult.success ? 'COPIED' : 'FAILED'}</span>
                                            </div>
                                            <div className="result-details">
                                                <div><strong>From:</strong> {lastCopyResult.sourceLabel}</div>
                                                <div><strong>To:</strong> {lastCopyResult.targetLabel}</div>
                                                <div><strong>Article:</strong> {lastCopyResult.articleId}</div>
                                                {!lastCopyResult.success && lastCopyResult.message && (
                                                    <div className="result-error">{lastCopyResult.message}</div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Show source label info when validated */}
                                    {copyOldLabelInfo && (
                                        <div className="copy-source-info">
                                            <h4>Source Label Info</h4>
                                            <div className="source-info-table">
                                                <div className="source-info-row">
                                                    <span className="source-info-label">Label Code:</span>
                                                    <span className="source-info-value">{copyOldLabelCode}</span>
                                                </div>
                                                <div className="source-info-row">
                                                    <span className="source-info-label">Type:</span>
                                                    <span className="source-info-value">{copyOldLabelInfo.type || copyOldLabelInfo.labelModel}</span>
                                                </div>
                                                <div className="source-info-row">
                                                    <span className="source-info-label">Store:</span>
                                                    <span className="source-info-value">{copyOldLabelInfo.store}</span>
                                                </div>
                                                <div className="source-info-row highlight">
                                                    <span className="source-info-label">Article ID:</span>
                                                    <span className="source-info-value">
                                                        {Array.isArray(copyOldLabelInfo.articleId)
                                                            ? copyOldLabelInfo.articleId.join(', ')
                                                            : copyOldLabelInfo.articleId || 'No product assigned'}
                                                    </span>
                                                </div>
                                                {copyOldLabelInfo.battery && (
                                                    <div className="source-info-row">
                                                        <span className="source-info-label">Battery:</span>
                                                        <span className="source-info-value">{copyOldLabelInfo.battery}</span>
                                                    </div>
                                                )}
                                                {copyOldLabelInfo.networkStatus !== undefined && (
                                                    <div className="source-info-row">
                                                        <span className="source-info-label">Status:</span>
                                                        <span className="source-info-value">{copyOldLabelInfo.networkStatus ? 'Online' : 'Offline'}</span>
                                                    </div>
                                                )}
                                            </div>
                                            {copyNewLabelCode.length === 12 && (
                                                <div className="copy-ready-message">
                                                    Ready to copy to <strong>{copyNewLabelCode}</strong>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Placeholder when no result and no source */}
                                    {!lastCopyResult && !copyOldLabelInfo && (
                                        <div className="no-results-placeholder">
                                            <p>Scan a source label to see its info.</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="no-results-placeholder">
                                    <p>{assignTab === 'copy' ? 'Scan a source label to see its info.' : 'Results will appear here after assigning a product.'}</p>
                                </div>
                            )}
                        </div>
                    </div>

                </div>

            {/* Assignment Leaderboard Modal */}
            {showLeaderboard && showAssignLeaderboard && (
                <div className="leaderboard-modal-overlay" onClick={() => setShowAssignLeaderboard(false)}>
                    <div className="leaderboard-modal" onClick={e => e.stopPropagation()}>
                        <div className="leaderboard-header">
                            <h2>🏆 Assignment Leaderboard</h2>
                            <button className="modal-close-btn" onClick={() => setShowAssignLeaderboard(false)}>×</button>
                        </div>
                        <div className="leaderboard-content">
                            {leaderboardLoading ? (
                                <p className="loading-text">Loading leaderboard...</p>
                            ) : assignLeaderboard.length === 0 ? (
                                <p className="empty-text">No assignment data yet</p>
                            ) : (
                                <table className="leaderboard-table">
                                    <thead>
                                        <tr>
                                            <th>Rank</th>
                                            <th>User</th>
                                            <th>New</th>
                                            <th>Copies</th>
                                            <th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {assignLeaderboard.map((user, idx) => (
                                            <tr key={user._id || idx} className={idx < 3 ? `rank-${idx + 1}` : ''}>
                                                <td className="rank-cell">
                                                    {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                                                </td>
                                                <td>{user.userName || user.userEmail?.split('@')[0] || 'Unknown'}</td>
                                                <td>{user.assigns || 0}</td>
                                                <td>{user.copies || 0}</td>
                                                <td className="total-cell">{user.total || 0}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="leaderboard-footer">
                            <button className="close-btn" onClick={() => setShowAssignLeaderboard(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Label Conflict Alert Modal (405 Error) */}
            {labelConflict && (
                <div className="label-conflict-modal-overlay" onClick={() => setLabelConflict(null)}>
                    <div className="label-conflict-modal" onClick={e => e.stopPropagation()}>
                        <div className="label-conflict-header">
                            <h2>⚠️ Label Conflict</h2>
                            <button className="modal-close-btn" onClick={() => setLabelConflict(null)}>×</button>
                        </div>
                        <div className="label-conflict-content">
                            <div className="conflict-icon">🏷️</div>
                            <p className="conflict-label-code">Label: <strong>{labelConflict.labelCode}</strong></p>
                            <p className="conflict-message">{labelConflict.message}</p>
                            {labelConflict.conflictStore && (
                                <p className="conflict-store">Assigned to Store: <strong>{labelConflict.conflictStore}</strong></p>
                            )}
                            <p className="conflict-suggestion">{labelConflict.suggestion}</p>
                        </div>
                        <div className="label-conflict-footer">
                            <button className="conflict-ok-btn" onClick={() => setLabelConflict(null)}>OK</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AssignLabelPanel;
